import { z } from 'zod';
import type { SubmitCredentialingRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useZodForm } from '@/shared/lib/forms';
import { dobField, zipField } from '@/shared/lib/validators';

const attestationSectionSchema = z.object({
  answers: z.array(z.boolean()),
  explanation: z.string().nullish(),
});

const credentialingSchema: z.ZodType<SubmitCredentialingRequestT> = z.object({
  mailing_address_street: z.string().trim().min(1, 'Street address is required'),
  mailing_address_city: z.string().trim().min(1, 'City is required'),
  mailing_address_state: z.string().trim().min(1, 'State is required'),
  mailing_address_zip: zipField,
  date_of_birth: dobField,
  headshot_file_id: z.string().nullish(),
  application_data: z.object({
    attestation_professional_liability: attestationSectionSchema.optional(),
    attestation_physical_mental_health: attestationSectionSchema.optional(),
    attestation_disciplinary_license_dea: attestationSectionSchema.optional(),
    attestation_disciplinary_membership: attestationSectionSchema.optional(),
    attestation_compliance_with_laws: attestationSectionSchema.optional(),
  }),
});

export interface AttestationSectionMeta {
  key: string;
  fieldName: string;
  title: string;
  questions: readonly string[];
}

export const ATTESTATION_SECTIONS: readonly AttestationSectionMeta[] = [
  {
    key: 'professional_liability',
    fieldName: 'application_data.attestation_professional_liability',
    title: 'Professional Liability',
    questions: [
      'Are you authorized to work in the US?',
      'Have any professional liability/malpractice lawsuits been brought against you in the last seven years?',
      'Have any claims, incidents, or notices of intent to file a claim been made/settled prior to a lawsuit in the last seven years?',
      'Are you aware of any pending claims or notices of intent to sue, including those in arbitration?',
      'Are any limitations placed on your coverage scope, or was insurance declined, canceled, not renewed, or subjected to an involuntary surcharge/deductible?',
      'Have you had any professional liability insurance rate increases due to your claim volume or nature?',
    ],
  },
  {
    key: 'physical_mental_health',
    fieldName: 'application_data.attestation_physical_mental_health',
    title: 'Physical & Mental Health',
    questions: [
      'Do you have any physical, mental health, or substance use condition that currently impairs (or could impair) your ability to practice competently and ethically?',
      'Do you have any health condition requiring accommodation to perform requested clinical privileges?',
    ],
  },
  {
    key: 'disciplinary_license_dea',
    fieldName: 'application_data.attestation_disciplinary_license_dea',
    title: 'Disciplinary — License & DEA',
    questions: [
      'Has your professional license ever been voluntarily or involuntarily limited, suspended, revoked, denied, or subjected to probation, public reprimand, or fine?',
      'Have you received any notification to appear before a licensing agency for a hearing or complaint?',
      'Has your DEA certificate or controlled substance authorization ever been denied, revoked, suspended, or not renewed?',
    ],
  },
  {
    key: 'disciplinary_membership',
    fieldName: 'application_data.attestation_disciplinary_membership',
    title: 'Disciplinary — Membership & Privileges',
    questions: [
      'Have clinical privileges ever been limited, suspended, revoked, or subjected to probation at any health care organization?',
      'Has any request for specific clinical privileges ever been denied or granted with limitations?',
      'Has medical staff membership or any affiliation ever been limited, revoked, or subjected to probation?',
      'Have you been subjected to discipline or proctoring (other than initial proctoring) by any health care organization?',
      'Has a medical society or specialty board denied membership or subjected you to disciplinary action?',
      'Have you voluntarily relinquished membership, privileges, or licenses while under investigation or threat of investigation?',
      'Have you discontinued practice for one month or more for any reason other than routine vacation?',
    ],
  },
  {
    key: 'compliance_with_laws',
    fieldName: 'application_data.attestation_compliance_with_laws',
    title: 'Compliance with Laws',
    questions: [
      'Do you have any convictions, guilty/no-contest pleas, or formal charges for a crime (felony or misdemeanor, including DUI)?',
      'Do you have any pending or completed government actions or court cases involving allegations of failure to comply with laws or regulations?',
      'Do you have any current or past exclusion from participation in any federal or state healthcare program (e.g., Medicare/Medicaid)?',
      'Do you have any prior or pending litigation challenging or sanctioning your billing, admission, or treatment practices, including fraud and abuse proceedings?',
    ],
  },
] as const;

function buildDefaultAttestationAnswers(): SubmitCredentialingRequestT['application_data'] {
  const data: Record<string, { answers: (boolean | null)[]; explanation: null }> = {};
  for (const section of ATTESTATION_SECTIONS) {
    data[`attestation_${section.key}`] = {
      answers: section.questions.map(() => null),
      explanation: null,
    };
  }
  return data as SubmitCredentialingRequestT['application_data'];
}

/** Returns the keys of sections that have unanswered (null) questions. */
export function findUnansweredSections(
  appData: Record<string, { answers: (boolean | null)[] }>
): string[] {
  return ATTESTATION_SECTIONS.filter((section) => {
    const answers = appData[`attestation_${section.key}`]?.answers;
    return !answers || answers.some((a) => a === null);
  }).map((s) => s.title);
}

const DEFAULT_VALUES: SubmitCredentialingRequestT = {
  mailing_address_street: '',
  mailing_address_city: '',
  mailing_address_state: '',
  mailing_address_zip: '',
  date_of_birth: '',
  application_data: buildDefaultAttestationAnswers(),
};

export function useCredentialingForm() {
  const { form, validateDraft } = useZodForm<SubmitCredentialingRequestT>({
    schema: credentialingSchema,
    defaultValues: DEFAULT_VALUES,
  });

  return { form, validateDraft };
}
